<template>
  <div style="width: 100%">
    <div class="pp-toolbar pp-toolbar-transparent action-bar">
      <SpinnerApiLoading label="Loading API for descriptions"/>

      <div class="pp-toolbar-boxes">
        <input-checkbox class="config-box" v-model="autoHideUi" label="Auto-Hide UI" title="If true the UI controls automatically hide after a short while."/>
        <input-checkbox class="config-box" v-model="playsInline" label="Plays Inline" title="If true the player stays inline on iPhone instead of automatically going to fullscreen mode."/>
      </div>

      <div class="flex-spacer"></div>

      <button class="button" @click="removeCustomConfig()" :disabled="!hasCustomId" v-if="hasCustomId">
        Delete
      </button>
      <button class="button" @click="reset()" :disabled="!((hasAssetId || hasCustomId) && changed)">
        Reset
      </button>
      <button class="button" @click="persist()" :disabled="!changed || !value.metadata.title || !value.source.url" v-if="!hasAssetId">
        Save and Load
      </button>
      <button class="button" @click="showJsonView=!showJsonView">
        {{showJsonView ? 'Hide' : 'Show'}} JSON
      </button>
      <button class="button button-icon primary" @click="load()" :disabled="canLoad">
        <i class="icon">play_arrow</i>
        Load
      </button>
    </div>
    <div v-if="hasCustomId" class="description">
      You are editing custom configuration #{{customId}}.
    </div>
    <PlayerConfigJsonView :config="value" class="json-view" :class="{'show-json-view': showJsonView}" v-if="showJsonView"/>
    <PlayerConfigBasicConfig v-model="value" :advanced-mode="true" class="config" :custom="hasCustomId || !hasAssetId" v-else/>
  </div>
</template>

<script>
import PlayerConfigBasicConfig from './config/PlayerConfigBasicConfig';
import { createPlayerConfig, defaultPlayerConfig } from '../../utils/player';
import PlayerComponent from './PlayerComponent';
import SpinnerApiLoading from '../SpinnerApiLoading';
import PlayerConfigJsonView from './config/PlayerConfigJsonView';
import deepEqual from 'deep-equal';
import InputCheckbox from '@/components/forms/InputCheckbox.vue';

export default {
  name: 'player-configuration',
  extends: PlayerComponent,
  components: { InputCheckbox, PlayerConfigJsonView, SpinnerApiLoading, PlayerConfigBasicConfig },
  data () {
    return {
      value: null,
      originalValue: null,
      showJsonView: false,
      changed: false,
      changedSinceLoad: false,
      lastLoadedConfigString: null
    };
  },
  created () {
    this.loadFromQuery();
  },
  computed: {
    customId () {
      return this.$route.query.customId;
    },
    assetId () {
      return this.$route.query.assetId;
    },
    hasCustomId () {
      return this.customId !== null && this.customId !== undefined;
    },
    hasAssetId () {
      return this.assetId !== null && this.assetId !== undefined;
    },
    canLoad () {
      return !(this.value && this.value.source && this.value.source.url);
    },
    autoHideUi: {
      get () {
        return this.$store.state.player_settings.autoHideUi;
      },
      set (value) {
        this.$store.commit('player_settings/autoHideUi', value);
      }
    },
    playsInline: {
      get () {
        return this.$store.state.player_settings.playsInline;
      },
      set (value) {
        this.$store.commit('player_settings/playsInline', value);
      }
    }
  },
  watch: {
    $route (to, from) {
      this.loadFromQuery();
    },
    config: function (n, o) {
      this.loadFromConfig();
    },
    value: {
      deep: true,
      handler: function () {
        const v1 = JSON.parse(JSON.stringify(this.value));
        const v2 = JSON.parse(JSON.stringify(this.originalValue));
        this.changed = !deepEqual(v1, v2);
      }
    }
  },
  methods: {
    reset () {
      if (this.hasCustomId) {
        this.originalValue = Object.assign({}, defaultPlayerConfig(), this.$store.getters['assetdb/custom'](this.customId));
        this.value = Object.assign({}, defaultPlayerConfig(), this.$store.getters['assetdb/custom'](this.customId));
      } else if (this.hasAssetId) {
        const asset = this.$store.getters['assetdb/asset'](this.assetId);
        this.originalValue = Object.assign({}, defaultPlayerConfig(), asset.cfg);
        this.value = Object.assign({}, defaultPlayerConfig(), asset.cfg);
      } else {
        this.originalValue = defaultPlayerConfig();
        this.value = defaultPlayerConfig();
      }
    },
    persist () {
      this.$store.dispatch('assetdb/save', { cfg: this.value, id: this.customId }).then((id) => {
        this.changed = false;
        this.pushConfigPage(id);
      });
    },
    removeCustomConfig () {
      this.$store.dispatch('assetdb/delete', { id: this.customId }).then(() => {
        this.$router.push({
          name: 'player.demo'
        });
      });
    },
    pushConfigPage (customId) {
      const rawConfigString = JSON.stringify(createPlayerConfig(this.value, true));
      this.$router.push({
        path: '/player/config',
        query: {
          cfg: btoa(rawConfigString),
          customId: customId,
          assetId: this.assetId
        }
      });
    },
    load () {
      const player = this.ppPlayerRef.get();
      if (!player) return;

      const configString = JSON.stringify(this.value);
      const configChanged = configString !== this.lastLoadedConfigString;
      if (configChanged) {
        this.pushConfigPage(this.customId);
      }
      this.lastLoadedConfigString = configString;

      const config = createPlayerConfig(this.value, true);
      player.load(config);
    },
    configFromStorage () {
      if (this.hasCustomId) {
        return this.$store.getters['assetdb/custom'](this.customId);
      } else if (this.hasAssetId) {
        return this.$store.getters['assetdb/asset'](this.assetId);
      }
      return null;
    },
    loadFromQuery () {
      const defaultConfig = defaultPlayerConfig();
      const configInQuery = JSON.parse(atob(this.$route.query.cfg || '') || '{}');
      // set autoplay to true if we're starting from empty config
      if (!Object.keys(configInQuery).length) {
        configInQuery.autoplay = true;
      }
      const config = Object.assign({}, defaultConfig, configInQuery);
      const fromStorage = this.configFromStorage();

      if (fromStorage) {
        this.originalValue = JSON.parse(JSON.stringify(fromStorage));
      } else {
        this.originalValue = JSON.parse(JSON.stringify(config));
      }
      this.value = createPlayerConfig(config);
    },
    loadFromConfig () {
      const defaultConfig = defaultPlayerConfig();
      const configInQuery = this.config;
      const config = Object.assign({}, defaultConfig, configInQuery);
      const fromStorage = this.configFromStorage();

      if (fromStorage) {
        this.originalValue = JSON.parse(JSON.stringify(fromStorage));
      } else {
        this.originalValue = JSON.parse(JSON.stringify(config));
      }
      this.value = createPlayerConfig(config);
    }
  }
};
</script>

<style lang="scss" scoped>
  .config {
    margin-top: 1rem;
  }
  .action-bar {
    padding: 2rem
  }

  .description {
    padding: 1rem;
    padding-left: 3rem;
  }

  .json-view {
    display: none;
  }
  .show-json-view {
    display: block;
  }

  .pp-toolbar-boxes {
    display: flex;
    gap: 1em;
    margin-left: 0.5em;
  }
</style>
